import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Plus, Trash2, ShieldCheck, KeyRound } from 'lucide-react';
import adminService from '../../services/admin/admin.service';
import type { Role, PermissionCatalogue } from '../../types/admin/admin.types';
import Table, { type TableColumn } from '../../components/ui/Table';
import Badge from '../../components/ui/Badge';
import EmptyState from '../../components/ui/EmptyState';
import Button from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import { PageHeader, SearchBox, Modal, Field, inputClass, usePagedList } from './ui/AdminUI';

const RoleBuilderPage: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [catalogue, setCatalogue] = useState<PermissionCatalogue | null>(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [permRole, setPermRole] = useState<Role | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([adminService.listRoles(), adminService.permissionCatalogue()]);
      setRoles(r.data); setCatalogue(c.data);
    } catch (e: any) { toast.error(e?.response?.data?.message || 'Failed to load roles.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const { query, setQuery, pageItems, pagination, setPage } = usePagedList<Role>(
    roles, (r, q) => r.name.toLowerCase().includes(q) || (r.description || '').toLowerCase().includes(q), 10
  );

  const openPerms = async (role: Role) => setPermRole((await adminService.getRole(role.id)).data);

  const deleteRole = async (role: Role) => {
    if (!window.confirm(`Delete role "${role.name}"?`)) return;
    try { await adminService.deleteRole(role.id, true); toast.success('Role deleted.'); await load(); }
    catch (e: any) { toast.error(e?.response?.data?.message || 'Failed to delete.'); }
  };

  const columns: TableColumn<Role>[] = [
    { key: 'name', header: 'Role', render: (r) => (
      <div className="flex items-center gap-2">
        <span className="font-medium text-[var(--color-gray-800)]">{r.name}</span>
        {r.is_system && <Badge color="primary" variant="soft">system</Badge>}
      </div>
    ) },
    { key: 'desc', header: 'Description', render: (r) => <span className="text-[var(--color-gray-600)]">{r.description || <span className="text-[var(--color-gray-400)]">—</span>}</span> },
    { key: 'perms', header: 'Permissions', align: 'center', render: (r) => <Badge color="info" variant="soft">{r.permission_count ?? 0}</Badge> },
    { key: 'actions', header: '', align: 'right', width: 200, render: (r) => (
      <div className="flex items-center justify-end gap-1">
        <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); openPerms(r); }}><KeyRound size={14} className="mr-1" /> Permissions</Button>
        {!r.is_system && <button title="Delete" className="p-1.5 rounded hover:bg-[var(--color-error-light)] text-[var(--color-error)]" onClick={(e) => { e.stopPropagation(); deleteRole(r); }}><Trash2 size={15} /></button>}
      </div>
    ) },
  ];

  return (
    <div>
      <PageHeader title="Role Builder" subtitle="Create roles and grant permissions per template" actions={<Button size="sm" onClick={() => setCreateOpen(true)}><Plus size={16} className="mr-1" /> New Role</Button>} />
      <div className="mb-4 max-w-sm"><SearchBox value={query} onChange={setQuery} placeholder="Search roles…" /></div>

      {loading ? <div className="p-10 flex justify-center"><Spinner size={28} /></div> : (
        <Table columns={columns} rows={pageItems} rowKey={(r) => String(r.id)} minWidth={640} onRowClick={(r) => openPerms(r)}
          pagination={pagination} onPageChange={setPage}
          emptyState={<EmptyState title="No roles yet" description="Create a role and assign permissions." />} />
      )}

      <CreateRoleModal open={createOpen} onClose={() => setCreateOpen(false)} onSaved={async (role) => { setCreateOpen(false); await load(); if (role) openPerms(role); }} />
      {permRole && catalogue && (
        <PermissionMatrixModal role={permRole} catalogue={catalogue} onClose={() => setPermRole(null)} onSaved={async () => { setPermRole(null); await load(); }} />
      )}
    </div>
  );
};

const CreateRoleModal: React.FC<{ open: boolean; onClose: () => void; onSaved: (role?: Role) => void }> = ({ open, onClose, onSaved }) => {
  const [form, setForm] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm({ name: '', description: '' }); }, [open]);

  const submit = async () => {
    if (!form.name) return toast.error('Role name is required.');
    setSaving(true);
    try { const role = (await adminService.createRole({ name: form.name, description: form.description || undefined })).data; toast.success('Role created.'); onSaved(role); }
    catch (e: any) { toast.error(e?.response?.data?.message || 'Failed to create role.'); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={open} title="New Role" onClose={onClose}
      footer={<><Button size="sm" variant="secondary" onClick={onClose}>Cancel</Button><Button size="sm" onClick={submit} disabled={saving}>{saving ? <Spinner size={14} /> : 'Create'}</Button></>}>
      <div className="space-y-4">
        <Field label="Role name" required><input className={inputClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
        <Field label="Description"><input className={inputClass} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
      </div>
    </Modal>
  );
};

const PermissionMatrixModal: React.FC<{ role: Role; catalogue: PermissionCatalogue; onClose: () => void; onSaved: () => void }> = ({ role, catalogue, onClose, onSaved }) => {
  const [checked, setChecked] = useState<Set<number>>(new Set((role.permissions || []).map((p) => p.id)));
  const [saving, setSaving] = useState(false);
  const readOnly = role.is_system;

  const toggle = (id: number) => setChecked((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleGroup = (ids: number[], on: boolean) => setChecked((prev) => { const n = new Set(prev); ids.forEach((id) => on ? n.add(id) : n.delete(id)); return n; });

  const save = async () => {
    setSaving(true);
    try { await adminService.setRolePermissions(role.id, Array.from(checked)); toast.success('Permissions saved.'); onSaved(); }
    catch (e: any) { toast.error(e?.response?.data?.message || 'Failed to save.'); }
    finally { setSaving(false); }
  };

  return (
    <Modal open title={`Permissions — ${role.name}`} onClose={onClose} width="lg"
      footer={readOnly ? <Button size="sm" variant="secondary" onClick={onClose}>Close</Button> :
        <><Button size="sm" variant="secondary" onClick={onClose}>Cancel</Button><Button size="sm" onClick={save} disabled={saving}>{saving ? <Spinner size={14} /> : 'Save permissions'}</Button></>}>
      {readOnly && <div className="mb-4 text-sm text-[var(--color-primary)] bg-[var(--color-primary-50)] border border-[var(--color-primary-100)] rounded-[var(--radius-md)] px-3 py-2 flex items-center gap-2"><ShieldCheck size={16} /> System role — full access, permissions are fixed.</div>}

      <div className="space-y-5 max-h-[55vh] overflow-y-auto pr-1">
        <div>
          <div className="text-xs font-semibold text-[var(--color-gray-500)] uppercase mb-2">System</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {catalogue.system.map((p) => (
              <label key={p.id} className="flex items-center gap-2 text-sm border border-[var(--color-gray-200)] rounded-[var(--radius-md)] px-3 py-2">
                <input type="checkbox" className="!min-h-0 w-4 h-4" disabled={readOnly} checked={checked.has(p.id)} onChange={() => toggle(p.id)} />
                <span title={p.key}>{p.label || p.key}</span>
              </label>
            ))}
          </div>
        </div>

        {catalogue.templates.map((grp) => {
          const ids = grp.permissions.map((p) => p.id);
          const allOn = ids.length > 0 && ids.every((id) => checked.has(id));
          return (
            <div key={grp.template.id}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold text-[var(--color-gray-500)] uppercase">{grp.template.name}</div>
                {!readOnly && ids.length > 0 && (
                  <button className="text-xs text-[var(--color-primary)] hover:underline" onClick={() => toggleGroup(ids, !allOn)}>{allOn ? 'Clear all' : 'Select all'}</button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {grp.permissions.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 text-sm border border-[var(--color-gray-200)] rounded-[var(--radius-md)] px-3 py-1.5">
                    <input type="checkbox" className="!min-h-0 w-4 h-4" disabled={readOnly} checked={checked.has(p.id)} onChange={() => toggle(p.id)} />
                    <span title={p.key}>{p.action}</span>
                  </label>
                ))}
                {grp.permissions.length === 0 && <span className="text-xs text-[var(--color-gray-400)]">no permissions</span>}
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
};

export default RoleBuilderPage;
